import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { LessonEditStatus } from '../../../../types/planung-graphql-client-defs';
import { isNotEmpty } from '../../../../utils/typeguards';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';
import { LessonTableType } from '../../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType, VersionLessonFormType } from '../../types';

type EpochBasicsFormProps = {
  status: LessonTableType['editStatus'];
  preFilterSubjectContainers: boolean;
};

export const EpochBasicsForm = ({ status, preFilterSubjectContainers }: EpochBasicsFormProps) => {
  const { t } = useTranslation();

  const { values, setFieldTouched, setFieldValue, handleChange } = useFormikContext<
    LessonFormType | VersionLessonFormType
  >();

  const currentClass = values.lessonClasses && values.lessonClasses.length === 1 ? values.lessonClasses[0].class : null;

  const { lessons: lessonsData } = useDeputateLessons({});

  const { subjectContainerData, classesData } = useLoadBasicData({ pause: false });

  const lessons = lessonsData
    .filter((lesson) =>
      lesson.classes.some((c) => currentClass?.uuid === c.uuid && lesson.isSubjectContainerLesson === true),
    )
    .map((l) => l.subjectUuid);

  const subjectContainers = subjectContainerData?.subjectContainers
    .filter((sc) => {
      if (preFilterSubjectContainers) {
        if (sc.grades.length === 0) {
          return true;
        }
        if (isNotEmpty(currentClass?.grade)) {
          return sc.grades.includes(currentClass.grade);
        }
        return true;
      }
      return true;
    })
    .filter((sc) => {
      return !lessons.includes(sc.uuid);
    });

  const subjectContainerOptions: SelectOptionType[] =
    subjectContainers?.map((sc) => {
      return {
        label: `${sc.name} (${sc.grades.sort((a, b) => a - b).join(', ')})`,
        value: sc.uuid,
      };
    }) ?? [];

  return (
    <Grid>
      <GridRow spacingBottom='s'>
        <Select
          options={subjectContainerOptions}
          isSearchable
          readonly={status === LessonEditStatus.Blocked || !!values.uuid}
          name='subject'
          onChange={async (option) => {
            const opt = option as SingleValue<SelectOptionType>;
            await setFieldTouched('subject', true);
            if (opt) {
              const subjectContainer = subjectContainers?.find((s) => s.uuid === opt.value);
              await setFieldValue(
                'subject',
                {
                  uuid: subjectContainer?.uuid,
                  name: `${subjectContainer?.name} (${subjectContainer?.grades.sort((a, b) => a - b).join(', ')})`,
                },
                true,
              );
              await setFieldValue('validGrades', subjectContainer?.grades, true);

              if (values.lessonClasses) {
                const filteredClasses = values.lessonClasses.filter((lc) => {
                  const c = classesData?.classes.find((c) => c.uuid === lc.class.uuid);
                  const classGrade = c?.grade;
                  return classGrade ? subjectContainer?.grades.includes(classGrade) : false;
                });
                await setFieldValue('lessonClasses', filteredClasses, true);
              }
            } else {
              await setFieldValue('subject', null, true);
            }
          }}
          value={values.subject ? { value: values.subject?.uuid ?? '', label: values.subject?.name ?? '' } : undefined}
          label={t('lessonUnit.subjectContainer')}
        />
      </GridRow>

      <GridRow spacingTop='s'>
        <GridColumn width={6}>
          <Input
            value={values.subTitle}
            name='subTitle'
            onChange={handleChange}
            label={t('lesson.basics.subTitle')}
            readonly={status === LessonEditStatus.Blocked}
          />
        </GridColumn>
        <GridColumn width={6}>
          <Input
            value={values.comment}
            name='comment'
            onChange={handleChange}
            label={t('lesson.basics.comment')}
            readonly={status === LessonEditStatus.Blocked}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
